import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTelegram } from './telegram/useTelegram.js';

const Storefront = lazy(() => import('./routes/Storefront.js'));
const MerchantDashboard = lazy(() => import('./routes/MerchantDashboard.js'));
const ShopManage = lazy(() => import('./routes/ShopManage.js'));
const LandingPage = lazy(() => import('./routes/LandingPage.js'));

const queryClient = new QueryClient();

// STO-2: Outside Telegram in production, shop-scoped routes require Telegram Mini App launch context
function TelegramOnlyRoute({ children }: { children: React.ReactNode }) {
  const { initData } = useTelegram();

  if (!import.meta.env.DEV && !initData) {
    return (
      <div className="min-h-screen bg-background-primary text-foreground-primary flex items-center justify-center p-6">
        <div className="p-8 text-center max-w-sm mx-auto space-y-4 bg-surface border border-border-primary rounded-xl shadow-sm">
          <div className="w-12 h-12 rounded-full bg-brand-8 text-brand-base flex items-center justify-center mx-auto text-xl font-bold">
            ✈
          </div>
          <h2 className="text-lg font-semibold text-foreground-primary">Telegram Mini App Only</h2>
          <p className="text-sm text-foreground-secondary leading-relaxed">
            This store is designed to run exclusively inside Telegram. Please open it via your shop's Telegram bot menu.
          </p>
          <div className="pt-2">
            <Link
              to="/"
              className="text-xs text-brand-base hover:underline font-medium inline-block"
            >
              ← Back to FenCom Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export function App() {
  const { user } = useTelegram();

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <div className="min-h-screen bg-background-primary text-foreground-primary">
          {/* Dev toolbar strictly for local work (SET-18) */}
          {import.meta.env.DEV && (
            <div className="bg-brand-8 border-b border-brand-20 px-4 py-2 text-xs flex items-center justify-between text-foreground-secondary">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-brand-base animate-pulse" />
                <span>
                  <strong>Dev Mode:</strong> {user?.first_name || 'Guest'} (ID: {user?.id || 'none'})
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Link to="/" className="text-brand-base hover:underline font-medium">Home</Link>
                <span className="text-border-tertiary">|</span>
                <Link to="/merchant" className="text-brand-base hover:underline font-medium">Merchant</Link>
                <span className="text-border-tertiary">|</span>
                <Link to="/s/demo-shop" className="text-brand-base hover:underline font-medium">Storefront</Link>
                <span className="text-border-tertiary">|</span>
                <Link to="/s/demo-shop/manage" className="text-brand-base hover:underline font-medium">Manage</Link>
              </div>
            </div>
          )}

          <Suspense
            fallback={
              <div className="min-h-[50vh] flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-brand-base border-t-transparent rounded-full animate-spin" />
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route
                path="/merchant/*"
                element={
                  <TelegramOnlyRoute>
                    <MerchantDashboard />
                  </TelegramOnlyRoute>
                }
              />
              <Route
                path="/s/:slug/manage/*"
                element={
                  <TelegramOnlyRoute>
                    <ShopManage />
                  </TelegramOnlyRoute>
                }
              />
              <Route
                path="/s/:slug/*"
                element={
                  <TelegramOnlyRoute>
                    <Storefront />
                  </TelegramOnlyRoute>
                }
              />
              <Route
                path="*"
                element={
                  <div className="min-h-[50vh] flex items-center justify-center p-6">
                    <div className="p-8 text-center max-w-sm mx-auto space-y-3 bg-surface border border-border-primary rounded-xl">
                      <h2 className="text-base font-semibold">Page Not Found</h2>
                      <p className="text-xs text-foreground-secondary">
                        The requested page could not be found.
                      </p>
                      <Link to="/" className="text-xs text-brand-base hover:underline font-medium block pt-2">
                        ← Back to Home
                      </Link>
                    </div>
                  </div>
                }
              />
            </Routes>
          </Suspense>
        </div>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
